import React, { useState, useEffect } from "react";
import InputField from "common/customInputField";
import Button from "react-bootstrap/Button";
import FloorMapCard from "components/siteRepo/common/floorMapCard";
import Modal from "common/modal";
import GooglePlaces from "common/googlePlaces";
import { useNavigate } from "react-router-dom";
import AccountLookup from "common/accountLookup";
import StoreLookup from "common/storeLookup";
import { isPossiblePhoneNumber, parsePhoneNumber, formatPhoneNumber } from "react-phone-number-input";
import { geocodeByPlaceId } from "react-places-autocomplete";
import { useSelector, useDispatch } from "react-redux";
import SelectInput from "common/selectInput";
import { submitCreateNewSite } from "store/siteRepo/actionCreator";
import { showSpinner, hideSpinner } from "store/slices/spinner";
import { createValidationRules } from "../utils";
import { api } from "store/slices/api";
import { toast } from "react-toastify";
import {
  confirmBulkUpdateModal,
  updateSelectLayer,
  updateSiteDetailsFields,
  clearSiteFields,
} from "store/slices/siteRepo";

const SiteDetails = () => {
  const dispatch = useDispatch();

  const { data: statesList = [] } = api.useGetStatesQuery();
  const { projects } = useSelector((state) => state.siteRepo.createNewSite.selectedData);
  const [isShowDeleteLayerModal, setIsShowDeleteLayerModal] = useState(false);
  const [deleteLayerSelectedData, setDeleteLayerSelectedData] = useState({});
  const [isValidStoreLookup, setIsValidStoreLookup] = useState(true);
  const [storeData, setStoreData] = useState();
  const [siteNameValid, setSiteNameValid] = useState("");
  const [siteStreetValid, setSiteStreetValid] = useState("");
  const [siteAddressValid, setSiteAddressValid] = useState("");
  const [siteCityValid, setSiteCityValid] = useState("");
  const [siteZipValid, setSiteZipValid] = useState("");
  const [parentNameValid, setParentNameValid] = useState("");
  const [contactNameValid, setContactNameValid] = useState("");
  const [contactPhoneNumberValid, setContactPhoneNumberValid] = useState("");
  const [isShowProjectTabActive, setIsShowProjectTabActive] = useState(false);
  const [terrValid, setTerrValid] = useState("");
  const [accountValid, setAccountValid] = useState(true);
  const { siteDetails } = useSelector((state) => state.siteRepo.createNewSite);
  const { sub, storeCcn, terrCcn } = useSelector((state) => state.loggedInUser);
  const { data: storeInfo } = api.useGetStoreInfoQuery({ storeNumber: storeCcn });
  const [isCreating, setIsCreating] = useState(false);

  const validationRules = createValidationRules({
    setSiteNameValid,
    setTerrValid,
    setSiteStreetValid,
    setSiteCityValid,
    setSiteZipValid,
    setParentNameValid,
    setContactNameValid,
    setContactPhoneNumberValid,
  });

  useEffect(() => {
    dispatch(updateSiteDetailsFields({ key: "territory", value: terrCcn }));
  }, [terrCcn]);

  const isCreateCtaDisabled =
    isCreating ||
    !siteDetails.siteName?.length ||
    !siteDetails.siteAddress?.length ||
    !isValidStoreLookup ||
    !siteDetails.siteCity?.length ||
    !siteDetails.siteStreet?.length ||
    !siteDetails.siteZip?.length ||
    !siteDetails.territory?.length ||
    !accountValid;

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(clearSiteFields());
    dispatch(updateSiteDetailsFields({ key: "territory", value: terrCcn }));
  }, []);

  useEffect(() => {
    let temp = false;
    projects?.some((project) => {
      if (project.layersInfo?.length > 1) {
        temp = true;
      }
    });
    setIsShowProjectTabActive(temp);
  }, [projects]);

  const handleDeleteLayerModal = (isShow) => {
    if (isShow) {
      setIsShowDeleteLayerModal(true);
    } else {
      setIsShowDeleteLayerModal(false);
    }
  };

  const handleDeleteLayer = (data) => {
    setDeleteLayerSelectedData(data);
    setIsShowDeleteLayerModal(true);
  };

  const handleAddressChange = (address) => {
    const key = event.target.id;
    const value = address;
    if (key === "siteAddress") {
      if (value.length > 200) {
        setSiteAddressValid("Cannot exceed 200 characters.");
      } else if (value.length <= 200) {
        setSiteAddressValid("");
      }
    }
    dispatch(updateSiteDetailsFields({ key, value }));
  };

  const handleFieldsChange = (e, a) => {
    dispatch(updateSiteDetailsFields({ key: "siteState", value: a.id }));
  };

  const handleSiteDetailsChange = (e) => {
    const key = e.target.id;
    const value = key === "siteZip" ? e.target.value.replace(/\D/g, "") : e.target.value;
    dispatch(updateSiteDetailsFields({ key, value }));
    const validation = validationRules[key];

    if (validation) {
      validation.setter("");
      if (validation.maxLength && value.length > validation.maxLength) {
        validation.setter(validation.errorMsg);
      } else if (validation.requiredLength && value.length < validation.requiredLength) {
        validation.setter(validation.errorMsg);
      }
    }
  };

  const handleAddressSelect = async (_addr, placeId) => {
    dispatch(showSpinner());

    const addressComponent = { siteState: "", siteCity: "", siteZip: "", siteStreetNumber: "", siteStreetName: "" };
    let composedAddress;
    const results = await geocodeByPlaceId(placeId);
    const place = results[0];
    for (const component of place.address_components) {
      const componentType = component.types[0];
      switch (componentType) {
        case "administrative_area_level_1":
          addressComponent.siteState = component.short_name;
          break;
        case "locality":
          addressComponent.siteCity = component.long_name;
          break;
        case "administrative_area_level_2":
          if (addressComponent.siteCity === "") {
            addressComponent.siteCity = component.long_name;
          }
          break;
        case "postal_code":
          addressComponent.siteZip = component.long_name;
          break;
        case "street_number":
          addressComponent.siteStreetNumber = component.long_name;
          break;
        case "route":
          addressComponent.siteStreetName = component.short_name;
          break;
        case "establishment":
          addressComponent.siteStreetName = component.long_name;
          break;
      }
    }

    const { siteStreetNumber, siteStreetName, siteCity, siteState, siteZip } = addressComponent;
    composedAddress = `${siteStreetNumber || ""} ${siteStreetName || ""}, ${siteCity || ""}, ${siteState || ""} ${
      siteZip || ""
    }`;
    dispatch(updateSiteDetailsFields({ key: "siteAddress", value: composedAddress }));

    for (const component in addressComponent) {
      if (component === "siteState" || component === "siteCity" || component === "siteZip") {
        setTimeout(() => dispatch(updateSiteDetailsFields({ key: component, value: addressComponent[component] })), 0);
      }
    }
    dispatch(
      updateSiteDetailsFields({
        key: "siteStreet",
        value: `${addressComponent.siteStreetNumber} ${addressComponent.siteStreetName}`,
      }),
    );
    dispatch(hideSpinner());
  };

  const handleRemoveLayer = (selectionData) => {
    const { type, data } = selectionData;
    if (type === "layer") {
      dispatch(
        updateSelectLayer({ selectLayerProjectIndex: data.projectIndex, selectLayerLayerIndex: data.layerIndex }),
      );
    }
    handleDeleteLayerModal(false);
    if (!projects.length) {
      document.getElementById("select-project-tab").click();
    }
  };

  const createSite = () => {
    if (isCreating) {
      return;
    }
    const {
      siteName,
      siteAddress,
      siteCity,
      siteState,
      siteStreet,
      accountNumber,
      accountName,
      siteZip,
      parentNumber,
      phoneNumber,
      parentName,
      execName,
      execNumber,
      contactName,
      customerName,
      accountCity,
      accountStreet,
      accountZip,
      accountState,
      territory,
    } = siteDetails;
    const { storePhoneNumber, storeNumber, storeName } = storeData ?? storeInfo?.[0];

    const createSiteRequestObject = {
      siteName,
      siteAddress,
      siteCity,
      siteState,
      accountNumber,
      accountName,
      storeNumber,
      storeName,
      siteZip,
      siteStreet,
      parentNumber,
      phoneNumber,
      parentName,
      execName,
      execNumber,
      contactName,
      customerName,
      storePhone: storePhoneNumber,
      accountCity,
      accountStreet,
      accountZip,
      accountState,
      createdBy: sub,
      territory,
      projectDetailsList: projects.map((project) => {
        const { mprojectId, name } = project;
        return {
          mprojectId,
          projectId: "",
          createdBy: sub,
          name,
          layerInfoList: project.layersInfo
            .filter((layer) => layer.isSelected)
            .map((layer) => {
              const { name, index, shapeId } = layer;
              return {
                layerId: "",
                projectId: "",
                name,
                shapeId,
                createdBy: sub,
                pictureData: "",
                index,
                projectName: project.name,
                rooms: layer.rooms
                  ? layer.rooms.map((room) => {
                      const { area, perimeter, name, length, shapeId, width, type } = room;
                      return {
                        roomId: "",
                        layerId: "",
                        name,
                        perimeter,
                        area,
                        length,
                        shapeId,
                        width,
                        type,
                        products: room.products
                          ? room.products.map((product) => {
                              const {
                                id,
                                netArea,
                                quantity,
                                wasteRatio,
                                sellByBox,
                                unitsPerBox,
                                boxName,
                                taxable,
                                shapeId,
                                width,
                                boxCostPrice,
                                boxSalesPrice,
                                color,
                                costPrice,
                                desc,
                                manufacturer,
                                salesPrice,
                                trade,
                                type,
                                unit,
                                vendor,
                                vendorSku,
                              } = product;

                              return {
                                roomInfoId: "",
                                roomId: "",
                                netArea,
                                quantity,
                                wasteRatio,
                                sellByBox,
                                unitsPerBox,
                                id,
                                boxName,
                                taxable,
                                shapeId,
                                width,
                                boxCostPrice,
                                boxSalesPrice,
                                color,
                                costPrice,
                                desc,
                                manufacturer,
                                salesPrice,
                                trade,
                                type,
                                unit,
                                vendor,
                                vendorSku,
                              };
                            })
                          : [],
                      };
                    })
                  : [],
              };
            }),
          updateType: "new",
        };
      }),
    };

    createSiteRequestObject.projectDetailsList?.map((project) => {
      project.layerInfoList?.map((layer) => {
        let productsObj = {};
        layer.rooms?.map((room) => {
          room.products?.map((product) => {
            productsObj[product.id] = productsObj[product.id] ? productsObj[product.id] : {};

            if (product.netArea) {
              if (productsObj[product.id].netAreaSum) {
                productsObj[product.id].netAreaSum += parseFloat(product.netArea);
              } else {
                productsObj[product.id].netAreaSum = parseFloat(product.netArea);
              }
            }

            if (product.quantity) {
              if (productsObj[product.id].quantitySum) {
                productsObj[product.id].quantitySum += parseFloat(product.quantity);
              } else {
                productsObj[product.id].quantitySum = parseFloat(product.quantity);
              }
            }

            if (product.sellByBox && productsObj[product.id] && productsObj[product.id].sellByBox === undefined) {
              productsObj[product.id].sellByBox = product.sellByBox;
            }
          });
        });

        layer.rooms?.map((room) => {
          room.products?.map((product) => {
            if (product.sellByBox) {
              let tempActualUsage =
                Math.ceil(parseFloat(productsObj[product.id].quantitySum) / parseFloat(product.unitsPerBox)) *
                parseFloat(product.unitsPerBox);
              product.actualUsage = tempActualUsage.toFixed(2);
              product.waste =
                ((parseFloat(product.actualUsage) - productsObj[product.id].netAreaSum) /
                  productsObj[product.id].netAreaSum) *
                100;
              product.modActualUsage = null;
              product.modWaste = null;
            } else {
              product.actualUsage = parseFloat(productsObj[product.id].quantitySum).toFixed(2);
              product.waste =
                ((productsObj[product.id].quantitySum - productsObj[product.id].netAreaSum) /
                  productsObj[product.id].quantitySum) *
                100;
              product.modActualUsage = null;
              product.modWaste = null;
            }
            if (product.unit !== "SF" && product.unit !== "LF") {
              if (product.unit === "SY") {
                product.actualUsage = Math.round(product.actualUsage * 9);
                product.unit = "SF";
              } else if (product.unit === "LY") {
                product.actualUsage = Math.round(product.actualUsage * 3);
                product.unit = "LF";
              }
            }
          });
        });
      });
    });

    let valid = true;
    setSiteNameValid("");
    setTerrValid("");
    setSiteStreetValid("");
    setSiteCityValid("");
    setSiteAddressValid("");
    setParentNameValid("");
    setContactNameValid("");
    setContactPhoneNumberValid("");
    if (siteStreet.length > 100) {
      valid = false;
      setSiteStreetValid("Cannot exceed 100 characters.");
      window.scrollTo(0, 0);
    }
    if (siteCity.length > 50) {
      valid = false;
      setSiteCityValid("Cannot exceed 50 characters.");
      window.scrollTo(0, 0);
    }
    if (siteZip.length > 10) {
      valid = false;
      setSiteZipValid("Cannot exceed 10 characters.");
      window.scrollTo(0, 0);
    }
    if (siteAddress.length > 200) {
      valid = false;
      setSiteAddressValid("Cannot exceed 100 characters.");
      window.scrollTo(0, 0);
    }
    if (parentName?.length > 100) {
      valid = false;
      setParentNameValid("Cannot exceed 100 characters.");
      var elem = document.getElementById("parentSectionScroll");
      elem.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (contactName?.length > 61) {
      valid = false;
      setContactNameValid("Cannot exceed 61 characters.");
      var elem = document.getElementById("parentSectionScroll");
      elem.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (phoneNumber) {
      if (!isPossiblePhoneNumber("+1" + phoneNumber)) {
        valid = false;
        setContactPhoneNumberValid("Invalid Phone Number.");
        var elem = document.getElementById("parentSectionScroll");
        elem.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        let result = parsePhoneNumber("+1" + phoneNumber);
        createSiteRequestObject.phoneNumber = result.nationalNumber;
      }
    }

    if (siteName.length > 40) {
      valid = false;
      setSiteNameValid("Cannot exceed 40 characters.");
      window.scrollTo(0, 0);
    } else if (territory.length < 6) {
      valid = false;
      setTerrValid("Please enter 6 digits");
    } else if (!territory || territory === "") {
      valid = false;
      setTerrValid("Please enter Territory Number");
    }

    if (valid && isValidStoreLookup) {
      setIsCreating(true);

      toast
        .promise(dispatch(submitCreateNewSite(createSiteRequestObject)), {
          pending: "Creating Site",
          success: "Site created successfully",
          error: "Could not create site, please try again.",
        })
        .then((res) => {
          navigate(`/siteRepo/site/${res.siteId}`);
          if (createSiteRequestObject?.projectDetailsList?.length > 0) {
            dispatch(confirmBulkUpdateModal(true));
          }
        })
        .catch(() => {})
        .finally(() => setIsCreating(false));
    }
  };

  const onAccountNumberChange = (accNo, isValid, accountData) => {
    setAccountValid(isValid);
    dispatch(updateSiteDetailsFields({ key: "accountNumber", value: accNo }));
    if (isValid) {
      setAditionalAccountFields(accountData);
    }
  };

  const onAccountNameChange = (accName, isValid, accountData) => {
    dispatch(updateSiteDetailsFields({ key: "accountName", value: accName }));
    if (isValid) {
      setAditionalAccountFields(accountData);
    }
  };

  const setAditionalAccountFields = (accountData) => {
    for (const fields in accountData) {
      let field = fields;
      if (fields === "city") {
        field = "accountCity";
      } else if (fields === "street") {
        field = "accountStreet";
      } else if (fields === "zip") {
        field = "accountZip";
      } else if (fields === "state") {
        field = "accountState";
      } else if (fields === "parentName") {
        field = "parentName";
      } else if (fields === "parentNumber") {
        field = "parentNumber";
      } else {
        field = fields;
      }
      setTimeout(() => dispatch(updateSiteDetailsFields({ key: field, value: accountData[fields] })), 0);
    }
  };

  return (
    <div className="create-site-details-container d-flex flex-column justify-content-between">
      <div className="search-form-container px-0 border-0">
        <div className="search-form-container__fields">
          <form className="search-form-container__fields__form">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label={"Site Name"}
                  id="siteName"
                  placeholder="Enter a site name"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteDetails.siteName}
                  onChange={(e) => handleSiteDetailsChange(e)}
                  disabled={false}
                  isRequired={true}
                  errorMsg={siteNameValid}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <GooglePlaces
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  label={"Site Address"}
                  id="siteAddress"
                  isRequired={true}
                  onChange={handleAddressChange}
                  value={siteDetails.siteAddress}
                  onSelect={handleAddressSelect}
                  placeholder="Choose your site address"
                  debounce={1000}
                  errorMsg={siteAddressValid}
                />
                {siteAddressValid && (
                  <div className="form-group">
                    <p className="error-etext error-undefined mb-0">{siteAddressValid}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label={"Street"}
                  id="siteStreet"
                  placeholder={"Enter Street"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteDetails.siteStreet}
                  onChange={(e) => handleSiteDetailsChange(e)}
                  disabled={false}
                  isRequired={true}
                  errorMsg={siteStreetValid}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label={"City"}
                  id="siteCity"
                  placeholder={"Enter City"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteDetails.siteCity}
                  onChange={(e) => handleSiteDetailsChange(e)}
                  disabled={false}
                  isRequired={true}
                  errorMsg={siteCityValid}
                />
              </div>
            </div>
            <div className="row" id="taxabilityScroll">
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <SelectInput
                  options={statesList}
                  label={"State"}
                  defaultValue={"Select a state"}
                  value={siteDetails.siteState}
                  disabled={false}
                  isRequired={true}
                  id="siteState"
                  handleSelectInput={(e, value) => {
                    handleFieldsChange(e, value);
                  }}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label={"Zip"}
                  id="siteZip"
                  placeholder={"Enter Zip"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteDetails.siteZip}
                  onChange={(e) => handleSiteDetailsChange(e)}
                  disabled={false}
                  isRequired={true}
                  errorMsg={siteZipValid}
                />
              </div>
            </div>

            <AccountLookup
              numberFieldChangeCallback={onAccountNumberChange}
              nameFieldChangeCallback={onAccountNameChange}
            />

            <div className="row" id="parentSectionScroll">
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Parent Number"
                  id="parentNumber"
                  placeholder={"Enter Parent Number"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteDetails.parentNumber ?? ""}
                  onChange={(e) => handleSiteDetailsChange(e)}
                  disabled={false}
                  isRequired={false}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Parent Name"
                  id="parentName"
                  placeholder={"Enter Parent Name"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteDetails.parentName ?? ""}
                  onChange={(e) => handleSiteDetailsChange(e)}
                  disabled={false}
                  isRequired={false}
                  errorMsg={parentNameValid}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label={"Contact Phone Number"}
                  id="phoneNumber"
                  placeholder={"Enter Contact Phone Number"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={
                    siteDetails.phoneNumber
                      ? siteDetails.phoneNumber.length > 1
                        ? formatPhoneNumber("+1" + siteDetails.phoneNumber)
                        : siteDetails.phoneNumber
                      : ""
                  }
                  onChange={(e) => handleSiteDetailsChange(e)}
                  disabled={false}
                  isRequired={false}
                  errorMsg={contactPhoneNumberValid}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label={"Contact Name"}
                  id="contactName"
                  placeholder={"Enter Contact Name"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteDetails.contactName}
                  onChange={(e) => handleSiteDetailsChange(e)}
                  disabled={false}
                  isRequired={false}
                  errorMsg={contactNameValid}
                />
              </div>
            </div>

            <StoreLookup
              numberFieldChangeCallback={(storeNumber, data) => {
                setStoreData((prev) => ({
                  ...(prev ?? storeInfo?.[0]),
                  storeNumber,
                  storePhoneNumber: data.storePhoneNumber,
                }));
              }}
              nameFieldChangeCallback={(storeName, isValid, data) => {
                setStoreData((prev) => {
                  const storeData = prev ?? storeInfo?.[0] ?? {};
                  const storePhoneNumber = (isValid ? data : prev).storePhoneNumber;
                  return { ...storeData, storeName, storePhoneNumber };
                });
              }}
              onStoreLookupValidation={(isValid) => {
                if (isValid !== isValidStoreLookup) {
                  setIsValidStoreLookup(isValid);
                }
              }}
              debounceValue={1000}
              defaultData={storeInfo?.[0] ?? {}}
              requiredFields={true}
            />

            <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
              <InputField
                type="text"
                label={"Territory Number"}
                id="territory"
                placeholder={"Enter Territory Number"}
                fieldClasses="form-control search-form-container__fields__form-group__item"
                value={siteDetails.territory}
                onChange={(e) => handleSiteDetailsChange(e)}
                disabled={false}
                isRequired={true}
                errorMsg={terrValid}
              />
            </div>
            <div className="row selected-projects">
              <div className="search-form-container__title d-flex align-items-center">
                <span className="search-form-container__title--text w-auto">Selected Projects</span>
                <span className="search-form-container__result-count">{projects.length}</span>
              </div>

              {isShowProjectTabActive ? (
                <div className="container-proj">
                  {projects.map((project, idx) => {
                    return (
                      <div className="project-container" key={idx + project.projectId + project.name}>
                        <div className="col-12 d-flex proj-header justify-content-between">
                          <div className="d-flex align-items-center">
                            <span className="item-header">Floor Plan (Project) Name: </span>
                            <span className="item-label">{project.name}</span>
                          </div>
                        </div>

                        <div className="row floors">
                          <div className="d-flex flex-column flex-md-row flex-wrap">
                            {project.layersInfo?.length &&
                              project.layersInfo.map((layerItem, index) => {
                                if (layerItem.isSelected) {
                                  return (
                                    <FloorMapCard
                                      key={index}
                                      layerIndex={layerItem.elementIndex}
                                      handleSelection={() => {}}
                                      project={project}
                                      isSiteDetails={true}
                                      projectView={true}
                                      isSelectable={false}
                                      removeLayerAction={handleDeleteLayer}
                                    />
                                  );
                                }
                              })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="d-flex flex-column flex-md-row flex-wrap">
                  {projects.map((project) => {
                    return (
                      project.layersInfo?.length &&
                      project.layersInfo.map((layerItem, index) => {
                        if (layerItem.isSelected) {
                          return (
                            <FloorMapCard
                              key={index}
                              layerIndex={layerItem.elementIndex}
                              handleSelection={() => {}}
                              project={project}
                              isSiteDetails={true}
                              isSelectable={false}
                              removeLayerAction={handleDeleteLayer}
                            />
                          );
                        }
                      })
                    );
                  })}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {isShowDeleteLayerModal && (
        <Modal
          title={"Are you Sure?"}
          handleSave={() => handleRemoveLayer({ ...deleteLayerSelectedData })}
          handleCancel={() => handleDeleteLayerModal(false)}
          size={"lg"}
          sectionName={""}
          bodyClass={"account-lookup-search-form"}
          closeButtonText={"Cancel"}
          saveButtonText={"Remove"}
          saveButtonClass={"btn-primary remove"}
        >
          <>
            <div className="container-fluid search-form-container border-0 m-0">
              <p>
                Are you sure you want to remove the{" "}
                <b>
                  {deleteLayerSelectedData.data.layerName} - {deleteLayerSelectedData.data.projectName}
                </b>{" "}
                unit from the selected list?
              </p>
            </div>
          </>
        </Modal>
      )}
      <div className="d-flex justify-content-between flex-column-reverse flex-md-row">
        <Button variant="outline-primary" onClick={() => navigate(`/siteRepo/createSite#select-project`)}>
          Back to projects
        </Button>
        <Button className="m-0" variant="primary" disabled={isCreateCtaDisabled} onClick={createSite}>
          Create Site
        </Button>
      </div>
    </div>
  );
};

export default SiteDetails;
